import PropTypes from "prop-types";
import { memo, useCallback } from "react";
import classNames from "classnames";
import IconTickboxTick from "app/pages/.shared/static/icons/IconTickboxTick";
import IconTickbox from "app/pages/.shared/static/icons/IconTickbox";
import "./checkbox.scss";

const CheckboxFormik = ({
	field,
	form,
	children,
	id,
	isRequired,
	disabled,
	onUpdate = () => {},
	grouped = true,
	size = 20,
	...restProps
}) => {
	const { name, value: formikValue } = field;
	const { setFieldValue, setFieldTouched, touched, errors, submitCount, setFieldError } = form;

	const onChange = useCallback(
		event => {
			const value = event.target.checked;

			if (grouped) {
				const set = new Set(field.value);
				if (value) {
					set.add(restProps.value);
				} else {
					set.delete(restProps.value);
				}
				setFieldValue(field.name, Array.from(set));
				setFieldTouched(field.name, true);
				onUpdate(Array.from(set), field.name);
			} else {
				setFieldValue(field.name, value);
				setFieldTouched(field.name, true);
			}
			if (value !== "") {
				setFieldError(name);
			}
		},
		[field, restProps.value, formikValue]
	);

	const checked = grouped ? formikValue.includes(restProps.value) : formikValue;

	const hasError = Boolean(errors?.[name] && (touched?.[name] || submitCount > 0));

	const checkboxClassName = classNames({
		checkbox: true,
		"checkbox--required": isRequired,
		"checkbox--error": hasError,
		"checkbox--checked": field.value || checked,
		"checkbox--disabled": disabled,
	});

	const iconCheckbox = (
		<div className="checkbox__icon">
			{checked ? (
				<IconTickboxTick height={size} width={size} />
			) : (
				<IconTickbox height={size} width={size} />
			)}
		</div>
	);

	return (
		<div className={checkboxClassName}>
			<input
				type="checkbox"
				checked={checked}
				id={id}
				name={name}
				className="checkbox__input"
				data-testid={restProps["data-testid"]}
				onChange={onChange}
				// Important : Ne pas supprimer ! Avec la mise à jour de Formik, un input de type checkbox affiche par défaut une case à cocher.
				// nous utilisons une icône spécifique, pas celle par défaut d'ou le display: "none"
				style={{ display: "none" }}
				{...restProps}
			/>
			<label className="checkbox__label" htmlFor={id}>
				<div className="checkbox__header">
					{iconCheckbox} <div className="checkbox__text">{children}</div>
				</div>
			</label>
		</div>
	);
};

CheckboxFormik.propTypes = {
	field: PropTypes.object,
	form: PropTypes.object,
	disabled: PropTypes.bool,
	isRequired: PropTypes.bool,
	name: PropTypes.string,
	id: PropTypes.string.isRequired,
	onUpdate: PropTypes.func,
	toggle: PropTypes.func,
	value: PropTypes.array,
	grouped: PropTypes.bool,
	size: PropTypes.number,
	"data-testid": PropTypes.string,
};

export default memo(CheckboxFormik);
